import { downloadPdf } from '../utils/downloadPdf'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import useBaseService from '../hooks/services/useBaseService'
import useNotification from '../hooks/useNotification'
import { useQueryClient } from '@tanstack/react-query'
import { entities } from '../constants/entities'
import { AuthContext } from './AuthContext'
import useQueryWithLoader from '../hooks/useQueryWithLoader'

const ReportContext = createContext()

export const ReportProvider = ({ children }) => {
  const [exportId, setExportId] = useState(null)
  // Fila de relatórios aguardando geração. Cada item é
  // { url, params?, headers? } e é processado um por vez, reutilizando o
  // polling + download automático já existente.
  const [pendingReports, setPendingReports] = useState([])
  const [isStartingReport, setIsStartingReport] = useState(false)
  const { get: baseServiceGet, post: baseServicePost } = useBaseService()
  const { showNotification } = useNotification()
  const { isAuthenticated } = useContext(AuthContext)
  const queryClient = useQueryClient()

  // 1. Recuperar exportId do localStorage de forma SEGURA
  useEffect(() => {
    const storedExportId = localStorage.getItem('report_exportId')

    // Verifica se existe E se NÃO é a string inválida "undefined" ou "null"
    if (
      storedExportId &&
      storedExportId !== 'undefined' &&
      storedExportId !== 'null'
    ) {
      setExportId(storedExportId)
    } else {
      // Limpa o lixo do localStorage se for inválido
      localStorage.removeItem('report_exportId')
    }
  }, [])

  // 2. Resetar TUDO quando o usuário deslogar
  useEffect(() => {
    if (!isAuthenticated) {
      setExportId(null)
      setPendingReports([])
      localStorage.removeItem('report_exportId')
      queryClient.removeQueries({ queryKey: ['pollReportStatus'] })
    }
  }, [isAuthenticated, queryClient])

  // 3. Polling do status
  const { data: pollStatus, isError } = useQueryWithLoader({
    queryKey: ['pollReportStatus', exportId],
    queryFn: async () => {
      // Proteção extra: se por algum motivo exportId for falsy, não faz a chamada
      if (!exportId) throw new Error('ID inválido')

      const response = await baseServiceGet(
        entities.reports.checkStatus(exportId)
      )
      return response.result
    },
    // Só habilita se exportId for um valor válido (não null, não undefined, não string "undefined")
    enabled:
      !!exportId &&
      exportId !== 'undefined' &&
      exportId !== 'null' &&
      isAuthenticated,
    showGlobalLoader: false,
    refetchInterval: (query) => {
      const status = query?.state?.data?.status
      if (!isAuthenticated) return false
      return status === 'completed' ||
        status === 'failed' ||
        status === 'cancelled'
        ? false
        : 2000
    },
    gcTime: 0,
    staleTime: 0,
    retry: (failureCount, error) => {
      if (error?.response?.status === 404 || error?.message === 'ID inválido')
        return false
      return failureCount < 1
    },
  })

  // 4. Efeito para lidar com a conclusão ou falha do relatório
  // 4. Efeito para lidar com a conclusão ou falha do relatório
  useEffect(() => {
    if (!pollStatus) return

    const handleCompleted = async () => {
      showNotification(
        'success',
        pollStatus.message ||
          'Relatório gerado com sucesso! O download iniciará em breve.'
      )

      await downloadPdf(pollStatus.file_path)

      setExportId(null)
      localStorage.removeItem('report_exportId')
      queryClient.removeQueries({ queryKey: ['pollReportStatus', exportId] })
    }

    if (pollStatus.status === 'completed') {
      handleCompleted()
    } else if (
      pollStatus.status === 'failed' ||
      pollStatus.status === 'cancelled'
    ) {
      const rawMessage =
        pollStatus.message ||
        (pollStatus.status === 'cancelled'
          ? 'Geração do relatório cancelada.'
          : 'Erro ao gerar o relatório.')

      // 🎯 TRATAMENTO AMIGÁVEL: Se a mensagem indicar falta de dados, usa 'warning' (amarelo)
      if (rawMessage.includes('Nenhum item encontrado')) {
        showNotification('warning', rawMessage)
      } else {
        // Se for um erro real de sistema, mantém o 'error' (vermelho)
        showNotification('error', rawMessage)
      }

      setExportId(null)
      localStorage.removeItem('report_exportId')
      queryClient.removeQueries({ queryKey: ['pollReportStatus', exportId] })
    }
  }, [pollStatus, showNotification, exportId, queryClient])

  // 5. Efeito para lidar com falhas na rede durante o polling
  useEffect(() => {
    if (isError) {
      showNotification(
        'error',
        'Falha ao verificar o status do relatório. Tente novamente.'
      )
      setExportId(null)
      localStorage.removeItem('report_exportId')
      queryClient.removeQueries({ queryKey: ['pollReportStatus', exportId] })
    }
  }, [isError, showNotification, exportId, queryClient])

  // 6. Enfileira relatórios para geração sequencial (ex.: protocolo de cada
  //    movimento confirmado num carregamento). Item: { url, params?, headers? }.
  const enqueueReports = useCallback((requests) => {
    const list = (Array.isArray(requests) ? requests : [requests]).filter(
      (request) => request?.url
    )

    if (list.length === 0) return

    setPendingReports((prev) => [...prev, ...list])
  }, [])

  // 7. Dispara o próximo relatório da fila quando não há exportação em andamento
  useEffect(() => {
    if (!isAuthenticated) return
    if (exportId) return
    if (isStartingReport) return
    if (pendingReports.length === 0) return

    const [nextReport, ...restReports] = pendingReports

    setIsStartingReport(true)
    setPendingReports(restReports)

    const startReport = async () => {
      try {
        const response = await baseServiceGet(
          nextReport.url,
          nextReport.params ?? {},
          { type: 'with-background', ...(nextReport.headers ?? {}) }
        )

        const generatedId = response?.result?.export_id

        if (generatedId) {
          setExportId(generatedId)
          localStorage.setItem('report_exportId', generatedId.toString())
        }
      } catch (error) {
        console.error('Erro ao gerar relatório da fila:', error)
      } finally {
        // Sem exportId iniciado, libera a fila para o próximo item.
        setIsStartingReport(false)
      }
    }

    startReport()
  }, [
    pendingReports,
    exportId,
    isStartingReport,
    isAuthenticated,
    baseServiceGet,
  ])

  const cancelReport = useCallback(async () => {
    if (!exportId || exportId === 'undefined') return

    try {
      await baseServicePost(
        entities.reports.cancel(exportId),
        {},
        { skipNotification: true }
      )
      showNotification('info', 'Solicitação de cancelamento enviada.')
    } catch (error) {
      console.error('Erro ao cancelar relatório:', error)
    } finally {
      localStorage.removeItem('report_exportId')
      setExportId(null)
      // Cancelar interrompe o lote inteiro, não só o relatório atual.
      setPendingReports([])
      queryClient.removeQueries({ queryKey: ['pollReportStatus', exportId] })
    }
  }, [exportId, baseServicePost, showNotification, queryClient])

  return (
    <ReportContext.Provider
      value={{
        setExportId,
        isExporting: !!exportId && exportId !== 'undefined',
        cancelReport,
        exportId,
        enqueueReports,
      }}>
      {children}
    </ReportContext.Provider>
  )
}

export const useReport = () => useContext(ReportContext)
